import type { INameTag } from '../../rendering/IRenderer';
import { createNameTag } from '../../rendering/utils/NameTag';
import { TweenHost } from '../../rendering/TweenHost';
import { Easing } from '../../rendering/Tween';
import type {
  AnimatedTextInstance,
  AnimatedTextOptions,
  TextAnimationStep,
  TextAnimationType,
} from './types';

const DEFAULT_SCRAMBLE_CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';

/* istanbul ignore next -- browser frame scheduling, swapped in tests */
let scheduleFrame = (cb: () => void): number => requestAnimationFrame(cb);
let cancelFrame = (id: number): void => cancelFrame(id);

/** Test-only hook to replace the frame scheduler with setTimeout-based fake timers. */
export function __setFrameSchedulerForTesting(
  schedule?: (cb: () => void) => number,
  cancel?: (id: number) => void,
): void {
  scheduleFrame = schedule ?? ((cb) => requestAnimationFrame(cb));
  cancelFrame = cancel ?? ((id) => cancelFrame(id));
}

function normalizeSteps(
  animation: AnimatedTextOptions['animation'],
  totalDurationMs: number,
): TextAnimationStep[] {
  if (!animation) return [];
  const list: Array<TextAnimationType | TextAnimationStep> = Array.isArray(animation)
    ? animation
    : [animation];

  const steps: TextAnimationStep[] = list.map((entry) =>
    typeof entry === 'string' ? { type: entry } : entry,
  );

  // If no explicit startAt, distribute steps sequentially across totalDurationMs.
  let cursor = 0;
  for (const step of steps) {
    const stepDuration = step.durationMs ?? Math.max(1, totalDurationMs / steps.length);
    if (step.startAt === undefined) {
      step.startAt = cursor / totalDurationMs;
      cursor += stepDuration;
    }
  }
  return steps;
}

function randomChar(charset: string): string {
  return charset[Math.floor(Math.random() * charset.length)];
}

function isNumberString(s: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(s);
}

/**
 * TextAnimator — substrate-agnostic animated text primitives.
 *
 * Builds on `createNameTag` (INameTag) so the same code works under Phaser
 * and Pixi. Supports per-text animations (typewriter, scramble, pop, shake,
 * wave, pulse, count, float, colorCycle) and returns a handle that can be
 * destroyed early.
 *
 * Phase H — animated text expansion (2026-07-05).
 */
export class TextAnimator {
  private scene: unknown;

  constructor(scene: unknown) {
    this.scene = scene;
  }

  public setScene(scene: unknown): void {
    this.scene = scene;
  }

  /**
   * Create an animated text instance. The returned handle exposes destroy()
   * and isAlive() for consumers like VFXSystem or CombatFeedbackManager.
   */
  public animate(opts: AnimatedTextOptions): AnimatedTextInstance {
    const {
      text,
      x,
      y,
      style = {},
      durationMs = 1500,
      holdMs = 400,
      autoDestroy = true,
      onComplete,
    } = opts;

    const label = createNameTag(this.scene, x, y, text, style);
    const startTime = performance.now();
    let destroyed = false;
    let killHandles: Array<() => void> = [];
    const rafHandles: number[] = [];

    const cleanup = () => {
      if (destroyed) return;
      destroyed = true;
      killHandles.forEach((kill) => kill());
      killHandles = [];
      rafHandles.forEach((id) => cancelFrame(id));
      rafHandles.length = 0;
      label.destroy();
      onComplete?.();
    };

    const steps = normalizeSteps(opts.animation, durationMs);

    for (const step of steps) {
      this.applyStep(step, label, text, x, y, opts, durationMs, startTime, {
        addKill: (kill) => killHandles.push(kill),
        addRaf: (id) => rafHandles.push(id),
      });
    }

    if (autoDestroy) {
      const timer = setTimeout(() => {
        // Let holdMs pass after the animation has finished.
        cleanup();
      }, durationMs + holdMs);
      killHandles.push(() => clearTimeout(timer));
    }

    return {
      destroy: cleanup,
      isAlive: () => !destroyed,
      setText: (newText: string) => {
        if (!destroyed) label.setText(newText);
      },
    };
  }

  private applyStep(
    step: TextAnimationStep,
    label: INameTag,
    text: string,
    x: number,
    y: number,
    opts: AnimatedTextOptions,
    totalDurationMs: number,
    startTime: number,
    handles: { addKill: (kill: () => void) => void; addRaf: (id: number) => void },
  ): void {
    const stepStartMs = startTime + (step.startAt ?? 0) * totalDurationMs;
    const stepDurationMs = step.durationMs ?? Math.max(1, totalDurationMs / Math.max(1, normalizeSteps(opts.animation, totalDurationMs).length));

    const schedule = (fn: () => void) => {
      const delay = stepStartMs - performance.now();
      if (delay <= 0) {
        fn();
      } else {
        const t = setTimeout(fn, delay);
        handles.addKill(() => clearTimeout(t));
      }
    };

    switch (step.type) {
      case 'typewriter':
        schedule(() => this.runTypewriter(label, text, stepDurationMs, opts.charRevealMs, handles));
        break;
      case 'scramble':
        schedule(() => this.runScramble(label, text, stepDurationMs, opts, handles));
        break;
      case 'pop':
        schedule(() => this.runPop(label, stepDurationMs, opts.popScale, handles));
        break;
      case 'pulse':
        schedule(() => this.runPulse(label, stepDurationMs, opts.pulseMagnitude, handles));
        break;
      case 'shake':
        schedule(() => this.runShake(label, x, y, stepDurationMs, opts.shakeIntensity, handles));
        break;
      case 'wave':
        schedule(() => this.runWave(label, x, y, stepDurationMs, opts, handles));
        break;
      case 'count':
        schedule(() => this.runCount(label, text, stepDurationMs, opts, handles));
        break;
      case 'float':
        schedule(() => this.runFloat(label, x, y, stepDurationMs, opts, handles));
        break;
      case 'fadeIn':
        schedule(() => this.runFadeIn(label, stepDurationMs, handles));
        break;
      case 'colorCycle':
        schedule(() => this.runColorCycle(label, stepDurationMs, opts.colorCycle, handles));
        break;
    }
  }

  private runTypewriter(
    label: INameTag,
    text: string,
    durationMs: number,
    charRevealMs: number | undefined,
    handles: { addRaf: (id: number) => void },
  ): void {
    const chars = Array.from(text);
    const revealMs = charRevealMs ?? Math.max(16, durationMs / Math.max(1, chars.length));
    label.setText('');
    let lastIndex = 0;
    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      const idx = Math.min(chars.length, Math.floor(elapsed / revealMs));
      if (idx !== lastIndex) {
        label.setText(chars.slice(0, idx).join(''));
        lastIndex = idx;
      }
      if (idx < chars.length) {
        handles.addRaf(scheduleFrame(tick));
      } else {
        label.setText(text);
      }
    };
    handles.addRaf(scheduleFrame(tick));
  }

  private runScramble(
    label: INameTag,
    text: string,
    durationMs: number,
    opts: AnimatedTextOptions,
    handles: { addRaf: (id: number) => void },
  ): void {
    const chars = Array.from(text);
    const charset = opts.scrambleCharset ?? DEFAULT_SCRAMBLE_CHARSET;
    const cycles = opts.scrambleCycles ?? 3;
    const msPerChar = durationMs / Math.max(1, chars.length);
    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      const settled = Math.min(chars.length, Math.max(0, Math.floor(elapsed / msPerChar)));
      let out = '';
      for (let i = 0; i < chars.length; i++) {
        if (i < settled) {
          out += chars[i];
        } else {
          const scrambles = Math.max(0, Math.floor(elapsed / (msPerChar / cycles)) - i * cycles);
          out += scrambles > 0 && chars[i] !== ' ' ? randomChar(charset) : chars[i];
        }
      }
      label.setText(out);
      if (settled < chars.length) {
        handles.addRaf(scheduleFrame(tick));
      } else {
        label.setText(text);
      }
    };
    handles.addRaf(scheduleFrame(tick));
  }

  private runPop(
    label: INameTag,
    durationMs: number,
    popScale: number | undefined,
    handles: { addKill: (kill: () => void) => void },
  ): void {
    const target = { scale: popScale ?? 1.4 };
    label.setScale(target.scale);
    const handle = TweenHost.add(target, {
      to: { scale: 1 },
      durationMs,
      ease: Easing.cubicOut,
      onComplete: () => label.setScale(1),
    });
    handles.addKill(() => handle.kill());
  }

  private runPulse(
    label: INameTag,
    durationMs: number,
    pulseMagnitude: number | undefined,
    handles: { addKill: (kill: () => void) => void; addRaf: (id: number) => void },
  ): void {
    const mag = pulseMagnitude ?? 0.15;
    const startTime = performance.now();
    const tick = () => {
      const t = (performance.now() - startTime) / durationMs;
      if (t >= 1) return;
      const scale = 1 + Math.sin(t * Math.PI * 2) * mag;
      label.setScale(scale);
      handles.addRaf(scheduleFrame(tick));
    };
    handles.addRaf(scheduleFrame(tick));
  }

  private runShake(
    label: INameTag,
    baseX: number,
    baseY: number,
    durationMs: number,
    shakeIntensity: number | undefined,
    handles: { addRaf: (id: number) => void },
  ): void {
    const intensity = shakeIntensity ?? 4;
    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      if (elapsed >= durationMs) {
        label.setPosition(baseX, baseY);
        return;
      }
      const dx = (Math.random() - 0.5) * intensity * 2;
      const dy = (Math.random() - 0.5) * intensity * 2;
      label.setPosition(baseX + dx, baseY + dy);
      handles.addRaf(scheduleFrame(tick));
    };
    handles.addRaf(scheduleFrame(tick));
  }

  private runWave(
    label: INameTag,
    baseX: number,
    baseY: number,
    durationMs: number,
    opts: AnimatedTextOptions,
    handles: { addRaf: (id: number) => void },
  ): void {
    const amplitude = opts.waveAmplitude ?? 8;
    const frequency = opts.waveFrequency ?? 2;
    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      if (elapsed >= durationMs) {
        label.setPosition(baseX, baseY);
        return;
      }
      const y = baseY + Math.sin(elapsed * 0.001 * Math.PI * 2 * frequency) * amplitude;
      label.setPosition(baseX, y);
      handles.addRaf(scheduleFrame(tick));
    };
    handles.addRaf(scheduleFrame(tick));
  }

  private runCount(
    label: INameTag,
    text: string,
    durationMs: number,
    opts: AnimatedTextOptions,
    handles: { addKill: (kill: () => void) => void; addRaf: (id: number) => void },
  ): void {
    const decimals = opts.countDecimals ?? 0;
    let from = opts.countFrom ?? 0;
    let to = opts.countTo ?? (isNumberString(text) ? parseFloat(text) : from);
    const prefix = opts.countPrefix ?? '';
    const suffix = opts.countSuffix ?? '';
    const target = { value: from };
    const handle = TweenHost.add(target, {
      to: { value: to },
      durationMs,
      ease: Easing.power2Out,
      onComplete: () => {
        label.setText(`${prefix}${to.toFixed(decimals)}${suffix}`);
      },
    });
    handles.addKill(() => handle.kill());

    // Drive text updates each frame via a cheap polling tween.
    const pollStart = performance.now();
    const poll = () => {
      const elapsed = performance.now() - pollStart;
      if (elapsed > durationMs + 50) return;
      label.setText(`${prefix}${target.value.toFixed(decimals)}${suffix}`);
      handles.addRaf(scheduleFrame(poll));
    };
    handles.addRaf(scheduleFrame(poll));
  }

  private runFloat(
    label: INameTag,
    baseX: number,
    baseY: number,
    durationMs: number,
    opts: AnimatedTextOptions,
    handles: { addKill: (kill: () => void) => void; addRaf: (id: number) => void },
  ): void {
    const distance = opts.floatDistance ?? 50;
    const fadeOut = opts.fadeOut ?? true;
    const target = { y: baseY, alpha: 1 };
    const startTime = performance.now();
    const handle = TweenHost.add(target, {
      to: { y: baseY - distance, alpha: fadeOut ? 0 : 1 },
      durationMs,
      ease: Easing.power2Out,
    });
    handles.addKill(() => handle.kill());

    const tick = () => {
      const elapsed = performance.now() - startTime;
      if (fadeOut) label.setAlpha(target.alpha);
      label.setPosition(baseX, target.y);
      if (elapsed < durationMs) {
        handles.addRaf(scheduleFrame(tick));
      }
    };
    handles.addRaf(scheduleFrame(tick));
  }

  private runFadeIn(
    label: INameTag,
    durationMs: number,
    handles: { addKill: (kill: () => void) => void },
  ): void {
    label.setAlpha(0);
    const target = { alpha: 0 };
    const handle = TweenHost.add(target, {
      to: { alpha: 1 },
      durationMs,
      ease: Easing.power2Out,
      onComplete: () => label.setAlpha(1),
    });
    handles.addKill(() => handle.kill());
  }

  private runColorCycle(
    label: INameTag,
    durationMs: number,
    colorCycle: number[] | undefined,
    handles: { addRaf: (id: number) => void },
  ): void {
    const colors = colorCycle ?? [0xffffff, 0xffdd44, 0xff8844];
    if (colors.length < 2 || typeof (label as any).setColor !== 'function') return;

    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      if (elapsed >= durationMs) return;
      const t = (elapsed / durationMs) * (colors.length - 1);
      const i0 = Math.floor(t);
      const i1 = Math.min(colors.length - 1, i0 + 1);
      const phase = t - i0;
      const c0 = colors[i0];
      const c1 = colors[i1];
      const r = Math.round(((c0 >> 16) & 0xff) + (((c1 >> 16) & 0xff) - ((c0 >> 16) & 0xff)) * phase);
      const g = Math.round(((c0 >> 8) & 0xff) + (((c1 >> 8) & 0xff) - ((c0 >> 8) & 0xff)) * phase);
      const b = Math.round((c0 & 0xff) + ((c1 & 0xff) - (c0 & 0xff)) * phase);
      const color = (r << 16) | (g << 8) | b;
      (label as any).setColor(color);
      handles.addRaf(scheduleFrame(tick));
    };
    handles.addRaf(scheduleFrame(tick));
  }
}
